import { useState, useCallback, useEffect, useRef } from "react";
import firebaseService from "firebase_service/firebaseService";
import { TicketFilters, ViewMode } from "../types";

const EMPTY_FILTERS: TicketFilters = {
  statuses: [],
  types: [],
  priorities: [],
  assignee: "",
  reporters: [],
  labels: [],
  targetVersion: "",
  updatedFrom: "",
  updatedTo: "",
};

type PersistedState = {
  filters: TicketFilters;
  showFilters: boolean;
  selectedAssignees: string[];
  onlyActive: boolean;
  onlyUnassigned: boolean;
  selectedPanelLabels: string[];
  showLabelPanel: boolean;
  viewMode: ViewMode;
};

const DEFAULTS: PersistedState = {
  filters: EMPTY_FILTERS,
  showFilters: false,
  selectedAssignees: [],
  onlyActive: true,
  onlyUnassigned: false,
  selectedPanelLabels: [],
  showLabelPanel: false,
  viewMode: "board",
};

function getStorageKey(): string | null {
  const userId = firebaseService.getUserId();
  if (!userId) return null;
  return `ticket-filters-${userId}`;
}

function readFromStorage(): PersistedState {
  try {
    const key = getStorageKey();
    if (!key) return DEFAULTS;
    const raw = localStorage.getItem(key);
    if (!raw) return DEFAULTS;

    const parsed = JSON.parse(raw) as Partial<PersistedState> | null;
    if (!parsed || typeof parsed !== "object") return DEFAULTS;

    const parsedFilters =
      parsed.filters && typeof parsed.filters === "object"
        ? parsed.filters
        : {};

    return {
      ...DEFAULTS,
      ...parsed,
      filters: { ...EMPTY_FILTERS, ...parsedFilters },
    };
  } catch {
    return DEFAULTS;
  }
}

function writeToStorage(state: PersistedState): void {
  try {
    const key = getStorageKey();
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // localStorage quota exceeded — in-memory state still works for the session
  }
}

export function useLocalTicketFilters() {
  const [state, setState] = useState<PersistedState>(readFromStorage);

  // Reset state when the Firebase user changes (account switch without remount)
  const userIdRef = useRef(firebaseService.getUserId());
  useEffect(() => {
    const currentUserId = firebaseService.getUserId();
    if (currentUserId !== userIdRef.current) {
      userIdRef.current = currentUserId;
      setState(readFromStorage());
    }
  });

  // Debounce localStorage writes so rapid updates coalesce
  const pendingWrite = useRef<PersistedState | null>(null);
  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleWrite = useCallback((nextState: PersistedState) => {
    pendingWrite.current = nextState;
    if (writeTimer.current) clearTimeout(writeTimer.current);
    writeTimer.current = setTimeout(() => {
      if (pendingWrite.current) writeToStorage(pendingWrite.current);
      pendingWrite.current = null;
      writeTimer.current = null;
    }, 300);
  }, []);

  // Flush pending write on unmount
  useEffect(() => {
    return () => {
      if (writeTimer.current) clearTimeout(writeTimer.current);
      if (pendingWrite.current) writeToStorage(pendingWrite.current);
    };
  }, []);

  function createFieldSetter<K extends keyof PersistedState>(field: K) {
    return (
      value:
        | PersistedState[K]
        | ((previous: PersistedState[K]) => PersistedState[K]),
    ) =>
      setState((previous) => {
        const resolved =
          typeof value === "function"
            ? (value as (previous: PersistedState[K]) => PersistedState[K])(
                previous[field],
              )
            : value;
        const next = { ...previous, [field]: resolved };
        scheduleWrite(next);
        return next;
      });
  }

  const setFilters = useCallback(createFieldSetter("filters"), []);
  const setShowFilters = useCallback(createFieldSetter("showFilters"), []);
  const setSelectedAssignees = useCallback(
    createFieldSetter("selectedAssignees"),
    [],
  );
  const setOnlyActive = useCallback(createFieldSetter("onlyActive"), []);
  const setOnlyUnassigned = useCallback(
    createFieldSetter("onlyUnassigned"),
    [],
  );
  const setSelectedPanelLabels = useCallback(
    createFieldSetter("selectedPanelLabels"),
    [],
  );
  const setShowLabelPanel = useCallback(
    createFieldSetter("showLabelPanel"),
    [],
  );
  const setViewMode = useCallback(createFieldSetter("viewMode"), []);

  return {
    filters: state.filters,
    setFilters,
    showFilters: state.showFilters,
    setShowFilters,
    selectedAssignees: state.selectedAssignees,
    setSelectedAssignees,
    onlyActive: state.onlyActive,
    setOnlyActive,
    onlyUnassigned: state.onlyUnassigned,
    setOnlyUnassigned,
    selectedPanelLabels: state.selectedPanelLabels,
    setSelectedPanelLabels,
    showLabelPanel: state.showLabelPanel,
    setShowLabelPanel,
    viewMode: state.viewMode,
    setViewMode,
    EMPTY_FILTERS,
  };
}
